import { Button, Flex, message, TablePaginationConfig } from "antd";
import CreateModal from "./components/CreateModal";
import EditModal from "./components/EditModal";
import { getColumns } from "./utils/table";
import { ExpenseCategory } from "@/types/expenseCategory";
import { DEFAULT_PAGINATION } from "@/constants/table";
import { useMemo, useState } from "react";
import useModal from "@/hooks/useModal";
import {
  useGetExpenseCategories,
  useGetExpenseCategoriesOptions,
} from "../../hooks/useExpenseCategories";
import {
  useDeleteExpenseCategory,
  useRestoreExpenseCategory,
} from "../../hooks/useExpenseCategories";
import { FilterCondition } from "@/components/table/hooks/useFilter";
import { SortCondition } from "@/components/table/hooks/useSort";
import Table, { FilterOptions } from "@/components/table";

const ExpenseCategories = () => {
  const [sort, setSort] = useState<SortCondition | undefined>();
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [pagination, setPagination] =
    useState<TablePaginationConfig>(DEFAULT_PAGINATION);

  const {
    isModalOpen: isAddModalOpen,
    openModal: openAddModal,
    closeModal: closeAddModal,
  } = useModal();
  const {
    modalData: editModalData,
    isModalOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal<ExpenseCategory>();

  const { mutate: deleteExpenseCategory } = useDeleteExpenseCategory();
  const { mutate: restoreExpenseCategory } = useRestoreExpenseCategory();
  const {
    data: expenseCategories,
    isFetching,
    refetch,
  } = useGetExpenseCategories({
    params: {
      include_inactive: true,
      sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
      filters: filters.length > 0 ? filters : undefined,
      limit: pagination?.pageSize,
      page: pagination?.current,
      offset: ((pagination?.current || 1) - 1) * (pagination?.pageSize || 10),
    },
  });

  // Get filter options
  const { data: options } = useGetExpenseCategoriesOptions({
    params: {
      only: "name,expense_type.id,expense_type.name",
      limit: -1,
    },
  });

  const filterOptions = useMemo(
    () => ({
      name: (options || []).reduce((acc, item) => {
        if (item.name && !acc.some((option) => option.value === item?.name)) {
          acc.push({
            text: item.name,
            value: item.name,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      expense_type_id: (options || []).reduce((acc, item) => {
        if (
          item.expense_type &&
          !acc.some((option) => option.value === item?.expense_type?.id)
        ) {
          acc.push({
            text: item.expense_type.name,
            value: item.expense_type.id,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
    }),
    [options]
  );

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      if (isActive) {
        restoreExpenseCategory(
          { id },
          {
            onSuccess: () => {
              message.success("Successfully activated expense category");
            },
          }
        );
      } else {
        deleteExpenseCategory(
          { id },
          {
            onSuccess: () => {
              message.success("Successfully deactivated expense category");
            },
          }
        );
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <Flex vertical gap={16}>
      <Flex justify="flex-end" align="center">
        <Button type="primary" onClick={openAddModal}>
          New Category
        </Button>
      </Flex>

      <Table
        columns={(props) =>
          getColumns({
            ...props,
            onEdit: openEditModal,
            onToggleActive: handleToggleActive,
            filterOptions,
          })
        }
        dataSource={expenseCategories?.data || []}
        loading={isFetching}
        pagination={{
          defaultPagination: {
            ...DEFAULT_PAGINATION,
            showSizeChanger: true,
          },
          dataPagination: expenseCategories?.pagination,
          onChange: setPagination,
        }}
        scroll={{
          x: "max-content",
        }}
        initialSort={sort}
        onSort={setSort}
        onFilter={setFilters}
      />

      <CreateModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onSuccess={() => refetch()}
      />
      <EditModal
        data={editModalData}
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onSuccess={() => refetch()}
      />
    </Flex>
  );
};

export default ExpenseCategories;
